import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Select,
  Badge,
  Card,
  LoadingSpinner,
} from '@/components/common';
import { MasterMappingImportModal } from '@/components/admin/MasterMappingImportModal';
import { useMasterMappings } from '@/hooks/useEligibility';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { getErrorMessage } from '@/services/api';
import { eligibilityService } from '@/services/eligibility';
import { vehicleService } from '@/services/vehicles';

// Every InsurerMasterMapping.MASTER_TYPE_CHOICES value — the filter used to
// stop at three, so city / pincode / fuel mappings could never be listed alone.
export const MASTER_TYPE_LABELS: Record<string, string> = {
  previous_insurer: 'Previous Insurer',
  fuel_type: 'Fuel Type',
  state: 'State',
  rto: 'RTO',
  city: 'City',
  pincode: 'Pincode',
  vehicle_make: 'Vehicle Make',
  vehicle_model: 'Vehicle Model',
  vehicle_variant: 'Vehicle Variant',
  vehicle_body_type: 'Vehicle Body Type',
};

// Mirrors master_mapping_io.LINE_OF_BUSINESS_TYPES: geography and the prior
// insurer serve every line; RTO, fuel and the vehicle masters only motor.
const SHARED_TYPES = ['previous_insurer', 'state', 'city', 'pincode'];
const LINE_OF_BUSINESS_TYPES: Record<string, string[]> = {
  motor: Object.keys(MASTER_TYPE_LABELS),
  health: SHARED_TYPES,
  life: SHARED_TYPES,
  sme: SHARED_TYPES,
  fire: SHARED_TYPES,
};

const lineOfBusinessOptions = [
  { value: '', label: 'All Lines of Business' },
  { value: 'motor', label: 'Motor' },
  { value: 'health', label: 'Health' },
  { value: 'life', label: 'Life' },
  { value: 'sme', label: 'SME' },
  { value: 'fire', label: 'Fire/Engineering' },
];

const mappingStatusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'mapped', label: 'Mapped (Active)' },
  { value: 'unmapped', label: 'Unmapped (Missing Code)' },
  { value: 'inactive', label: 'Inactive' },
];

const dataHealthOptions = [
  { value: '', label: 'All Data Health' },
  { value: 'missing_code', label: 'Missing Insurer Code' },
  { value: 'duplicate_code', label: 'Duplicate Insurer Code' },
];

// Types whose records sit under a make (and, for variants, a model).
const MAKE_CHILD_TYPES = ['vehicle_model', 'vehicle_variant'];

type FileKind = 'import-template' | 'export';

function DownloadMenu({
  label,
  icon,
  onPick,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  onPick: (fmt: 'csv' | 'xlsx') => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <Button variant="outline" leftIcon={icon} onClick={() => setOpen((o) => !o)} disabled={disabled}>
        {label}
      </Button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-40 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {(['csv', 'xlsx'] as const).map((fmt) => (
            <button
              key={fmt}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={() => { setOpen(false); onPick(fmt); }}
            >
              {fmt === 'csv' ? 'CSV' : 'Excel (.xlsx)'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MasterMappingsPage() {
  const [page, setPage] = useState(1);
  const [insurerFilter, setInsurerFilter] = useState('');
  const [masterTypeFilter, setMasterTypeFilter] = useState('');
  const [lobFilter, setLobFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState('');
  const [makeFilter, setMakeFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const showMakeFilter = MAKE_CHILD_TYPES.includes(masterTypeFilter);
  const showModelFilter = masterTypeFilter === 'vehicle_variant' && !!makeFilter;
  // Unmapped records only exist relative to one insurer and one type.
  const needsUnmappedScope = statusFilter === 'unmapped' && (!insurerFilter || !masterTypeFilter);

  const filterParams: Record<string, string> = {};
  if (insurerFilter) filterParams.insurer = insurerFilter;
  if (masterTypeFilter) filterParams.master_type = masterTypeFilter;
  if (lobFilter) filterParams.line_of_business = lobFilter;
  if (statusFilter) filterParams.mapping_status = statusFilter;
  if (healthFilter) filterParams.data_health = healthFilter;
  if (showMakeFilter && makeFilter) filterParams.make = makeFilter;
  if (showModelFilter && modelFilter) filterParams.model = modelFilter;

  const { data, isLoading, isError, error, refetch } = useMasterMappings(
    { ...filterParams, page, page_size: 20 },
    !needsUnmappedScope,
  );

  const { data: insurersLookup } = useInsurersLookup();
  const { data: makes } = useQuery({
    queryKey: ['vehicle-makes', undefined],
    queryFn: () => vehicleService.getMakes(),
    enabled: showMakeFilter,
  });
  const { data: models } = useQuery({
    queryKey: ['vehicle-models', makeFilter],
    queryFn: () => vehicleService.getModels(makeFilter),
    enabled: showModelFilter,
  });

  const mappings = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 20);

  const insurerOptions = [
    { value: '', label: 'All Insurers' },
    ...(insurersLookup || []).map((i) => ({ value: i.id, label: i.name })),
  ];

  const allowedTypes = lobFilter ? LINE_OF_BUSINESS_TYPES[lobFilter] : Object.keys(MASTER_TYPE_LABELS);
  const masterTypeOptions = [
    { value: '', label: 'All Types' },
    ...allowedTypes.map((value) => ({ value, label: MASTER_TYPE_LABELS[value] })),
  ];
  const makeOptions = [
    { value: '', label: 'All Makes' },
    ...(Array.isArray(makes) ? makes : []).map((m) => ({ value: m.id, label: m.name })),
  ];
  const modelOptions = [
    { value: '', label: 'All Models' },
    ...(Array.isArray(models) ? models : []).map((m) => ({ value: m.id, label: m.name })),
  ];

  // Every filter change starts again at page 1.
  const changeFilter = (set: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    set(e.target.value);
    setPage(1);
  };
  const changeMasterType = (value: string) => {
    setMasterTypeFilter(value);
    setMakeFilter('');
    setModelFilter('');
    setPage(1);
  };
  const changeLob = (value: string) => {
    setLobFilter(value);
    if (value && masterTypeFilter && !LINE_OF_BUSINESS_TYPES[value].includes(masterTypeFilter)) {
      changeMasterType('');
    }
    setPage(1);
  };

  const download = async (kind: FileKind, fmt: 'csv' | 'xlsx') => {
    setDownloading(true);
    try {
      // The export honours the filters on screen (with Unmapped it is a
      // fill-in sheet); the template is header-only, laid out for the type.
      const params: Record<string, string> =
        kind === 'export' ? filterParams : masterTypeFilter ? { master_type: masterTypeFilter } : {};
      await eligibilityService.downloadMappingsFile(kind, fmt, params);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const openImport = () => setIsImportOpen(true);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <nav className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            <span>Admin</span> / <span className="text-gray-700 dark:text-gray-200">Master Mappings</span>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Master Mappings</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Map platform records (previous insurers, fuel types, states, RTOs, cities, pincodes, vehicle makes, models, variants and body types) to each insurer&apos;s own codes
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DownloadMenu
            label="Download Template"
            icon={<DocumentTextIcon className="w-4 h-4" />}
            onPick={(fmt) => download('import-template', fmt)}
            disabled={downloading}
          />
          <DownloadMenu
            label="Export"
            icon={<ArrowDownTrayIcon className="w-4 h-4" />}
            onPick={(fmt) => download('export', fmt)}
            disabled={downloading || needsUnmappedScope}
          />
          <Button
            variant="primary"
            leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
            onClick={openImport}
          >
            Import
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            aria-label="Insurer"
            options={insurerOptions}
            value={insurerFilter}
            onChange={changeFilter(setInsurerFilter)}
            placeholder="All Insurers"
          />
          <Select
            aria-label="Line of business"
            options={lineOfBusinessOptions}
            value={lobFilter}
            onChange={(e) => changeLob(e.target.value)}
          />
          <Select
            aria-label="Master type"
            options={masterTypeOptions}
            value={masterTypeFilter}
            onChange={(e) => changeMasterType(e.target.value)}
          />
          <Select
            aria-label="Mapping status"
            options={mappingStatusOptions}
            value={statusFilter}
            onChange={changeFilter(setStatusFilter)}
          />
          <Select
            aria-label="Data health"
            options={dataHealthOptions}
            value={healthFilter}
            onChange={changeFilter(setHealthFilter)}
          />
          {showMakeFilter && (
            <Select
              aria-label="Make"
              options={makeOptions}
              value={makeFilter}
              onChange={(e) => { setMakeFilter(e.target.value); setModelFilter(''); setPage(1); }}
            />
          )}
          {showModelFilter && (
            <Select
              aria-label="Model"
              options={modelOptions}
              value={modelFilter}
              onChange={changeFilter(setModelFilter)}
            />
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {needsUnmappedScope ? (
          <div className="text-center py-12" data-testid="unmapped-hint">
            <TableCellsIcon className="w-12 h-12 mx-auto text-gray-400" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">
              Pick an insurer and a master type to list the records that have no insurer code yet.
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <div className="text-center py-12" role="alert">
            <h3 className="text-lg font-medium text-red-600 dark:text-red-400">Could not load mappings</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">{getErrorMessage(error)}</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : mappings.length === 0 ? (
          <div className="text-center py-12">
            <TableCellsIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No mappings found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Download the template, fill in the insurer&apos;s codes and import it.
            </p>
            <Button
              variant="primary"
              className="mt-4"
              leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
              onClick={openImport}
            >
              Import
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Insurer</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Master Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Platform Record</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Parent Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Insurer Code</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {mappings.map((mapping) => (
                  <tr key={mapping.id ?? `unmapped-${mapping.platform_record_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {mapping.insurer_name || mapping.insurer}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="info">
                        {MASTER_TYPE_LABELS[mapping.master_type] || mapping.master_type}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {mapping.platform_record_name || mapping.platform_record_id}
                      {mapping.platform_code && mapping.platform_code !== mapping.platform_record_name && (
                        <span className="ml-2 font-mono text-xs text-gray-500 dark:text-gray-400">{mapping.platform_code}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {mapping.parent_name || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-700 dark:text-gray-300">
                      {mapping.insurer_code.trim() || <span className="font-sans text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      {mapping.id === null ? (
                        <Badge variant="warning">Unmapped</Badge>
                      ) : (
                        <Badge variant={mapping.is_active ? 'success' : 'default'}>
                          {mapping.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages} ({data?.count || 0} results)
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <MasterMappingImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        masterType={masterTypeFilter || undefined}
      />
    </div>
  );
}

export default MasterMappingsPage;
